import Box from "@material-ui/core/Box";
import React, { useState, useEffect, useRef } from "react";

import clsx from "clsx";
import isSameDay from "date-fns/isSameDay";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Button from "@material-ui/core/Button";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import CloseIcon from "@material-ui/icons/Close";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Popover, {
  PopoverActions,
  PopoverProps
} from "@material-ui/core/Popover";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useTheme from "@material-ui/core/styles/useTheme";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import ICalendarDay from "interfaces/ICalendarDay";
import Breakpoints from "common/breakpoints";
import Loader from "components/Loader";
import Calendar from "components/DateRangeCalendar";
import { IDateRange } from "utils/facilityBlackouts";
import useStyles from "./styles";

enum DisablingStep {
  CHECK,
  CONFIRMATION
}

export enum EnableOption {
  DayOnly,
  DayAndAfter,
  Serie
}

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  anchorEl: HTMLButtonElement;
  selectedDay?: ICalendarDay;
  facilityName: string;
  onDateRangeChange: (range: IDateRange) => void;
  onDisable: (
    repeatance: racehorse360.FacilityBlackoutRepeatance,
    startDate: Date,
    endDate: Date
  ) => void;
  onEnable: (enableOption: EnableOption) => void;
  onClose: () => void;
  options: Partial<PopoverProps>;
}

// eslint-disable-next-line complexity
const BlackoutDatePopover = (props: Props) => {
  const {
    isOpen,
    isLoading,
    anchorEl,
    selectedDay,
    facilityName,
    onEnable,
    onDisable,
    onClose,
    onDateRangeChange,
    options
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const matchesDownSm600 = useMediaQuery(
    theme.breakpoints.down(Breakpoints.SM_600),
    {
      noSsr: true
    }
  );
  const matchesDownSS320 = useMediaQuery(
    theme.breakpoints.down(Breakpoints.SS_320),
    {
      noSsr: true
    }
  );
  const popoverActions = useRef<PopoverActions>();
  const [startDate, setStartDate] = useState<Date>(selectedDay?.date);
  const [endDate, setEndDate] = useState<Date>(selectedDay?.date);

  const [selectedEnableOption, setSelectedEnableOption] =
    useState<EnableOption>(EnableOption.DayOnly);
  const [step, setStep] = useState<DisablingStep>(DisablingStep.CHECK);
  const [repeatance, setRepeatance] =
    useState<racehorse360.FacilityBlackoutRepeatance>(
      selectedDay?.facilityBlackout?.repeatance
    );

  const isOneDayRange = isSameDay(startDate, endDate);

  const handleRepeatanceChange = (event: React.ChangeEvent<{ value }>) => {
    setRepeatance(event.target.value);
    onDateRangeChange({
      startDate,
      endDate,
      repeatance: event.target.value
    });
  };

  const handleClickNext = () => {
    setStep(DisablingStep.CONFIRMATION);
  };

  const handleClickBack = () => {
    setStep(DisablingStep.CHECK);
  };

  const handleClose = () => {
    setStep(DisablingStep.CHECK);
    onClose();
  };

  const handleConfirm = () => {
    setStep(DisablingStep.CHECK);
    onDisable(repeatance, startDate, endDate);
  };

  const handleClickEnable = () => {
    onEnable(selectedEnableOption);
  };

  const handleEnableOptionSelect = (enableOption: EnableOption) => () => {
    setSelectedEnableOption(enableOption);
  };

  const handleDateRangeChange = (range: IDateRange) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    onDateRangeChange({
      ...range,
      repeatance
    });
  };

  const handleCalendarOpen = () => {
    popoverActions?.current?.updatePosition();
  };

  useEffect(() => {
    setRepeatance(
      props.selectedDay?.facilityBlackout?.repeatance ||
        racehorse360.FacilityBlackoutRepeatance
          .FACILITY_BLACKOUT_REPEATANCE_DOES_NOT_REPEAT
    );
  }, [props.selectedDay]);

  if (!selectedDay) {
    return <></>;
  }

  const popoverProps: Partial<PopoverProps> = matchesDownSm600
    ? {
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        },
        transformOrigin: {
          vertical: "bottom",
          horizontal: "center"
        }
      }
    : { ...options };

  const isEnabling = !!selectedDay.facilityBlackout;
  const dateFrom = format(startDate, "MMMM d, yyyy");
  const dateTo = format(endDate, "MMMM d, yyyy");
  const dateFromShort = format(startDate, "MMM. d, yyyy");
  const dayOfWeek = format(startDate, "iiii");
  const isNoRepeat =
    repeatance ===
    racehorse360.FacilityBlackoutRepeatance
      .FACILITY_BLACKOUT_REPEATANCE_DOES_NOT_REPEAT;
  const isRepeatWeekly =
    repeatance ===
    racehorse360.FacilityBlackoutRepeatance.FACILITY_BLACKOUT_REPEATANCE_WEEKLY;

  return (
    <>
      <Popover
        action={popoverActions}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{
          paper: clsx(classes.paper, {
            [classes.paperEnabling]: isEnabling
          })
        }}
        {...popoverProps}
      >
        {isLoading && <Loader overlay />}
        {!isEnabling ? (
          <>
            {step === DisablingStep.CHECK ? (
              <Box className={classes.content}>
                <Box className={classes.header}>
                  <Typography className={classes.headerTitle}>
                    Disable Dates
                  </Typography>
                  <IconButton
                    size={"small"}
                    className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Calendar
                  onDateRangeChange={handleDateRangeChange}
                  defaultRange={{
                    startDate,
                    endDate,
                    repeatance
                  }}
                  disablePast={true}
                  onOpen={handleCalendarOpen}
                />

                <Box className={classes.repeatanceBlock}>
                  <FormControl>
                    <InputLabel shrink>Repeat Dates</InputLabel>
                    <Select
                      className={classes.repeatanceSelect}
                      value={repeatance}
                      onChange={handleRepeatanceChange}
                      displayEmpty
                    >
                      <MenuItem
                        value={
                          racehorse360.FacilityBlackoutRepeatance
                            .FACILITY_BLACKOUT_REPEATANCE_DOES_NOT_REPEAT
                        }
                      >
                        Does not repeat
                      </MenuItem>
                      <MenuItem
                        value={
                          racehorse360.FacilityBlackoutRepeatance
                            .FACILITY_BLACKOUT_REPEATANCE_WEEKLY
                        }
                      >
                        Weekly on {dayOfWeek}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box className={classes.controls}>
                  <Button
                    variant={"outlined"}
                    className={classes.control}
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                  <Button
                    variant={"outlined"}
                    className={clsx(classes.control, classes.nextControl)}
                    onClick={handleClickNext}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box className={classes.content}>
                <Box className={classes.header}>
                  <Typography className={classes.headerTitle}>
                    Summary
                  </Typography>
                  <IconButton
                    size={"small"}
                    className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box className={classes.dates}>
                  <Box className={classes.dateBox}>
                    <Typography className={classes.dateTitle}>
                      {isOneDayRange && isNoRepeat && <>Date</>}
                      {(isRepeatWeekly || !isOneDayRange) && <>Dates</>}
                    </Typography>
                    <Typography className={classes.dateBold}>
                      {isOneDayRange && isNoRepeat && dateFrom}
                      {isRepeatWeekly && (
                        <>
                          {isOneDayRange ? (
                            <>
                              Weekly on {dayOfWeek} beginning {""}
                              <Box
                                component={"span"}
                                className={classes.nowrap}
                              >
                                {dateFrom}
                              </Box>
                            </>
                          ) : (
                            <>
                              Weekly from {dayOfWeek} to{" "}
                              {format(endDate, "iiii")} beginning {""}
                              <Box
                                component={"span"}
                                className={classes.nowrap}
                              >
                                {format(startDate, "MMM. d, yyyy")} -{" "}
                                {format(endDate, "MMM. d, yyyy")}
                              </Box>
                            </>
                          )}
                        </>
                      )}
                      {!isRepeatWeekly && !isOneDayRange && (
                        <>
                          <Box component={"span"} className={classes.nowrap}>
                            {format(
                              startDate,
                              matchesDownSS320
                                ? "MMM. d, yyyy"
                                : "iiii, MMM. d, yyyy"
                            )}{" "}
                            -{" "}
                            {format(
                              endDate,
                              matchesDownSS320
                                ? "MMM. d, yyyy"
                                : "iiii, MMM. d, yyyy"
                            )}
                          </Box>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Typography className={classes.description}>
                  {isNoRepeat && (
                    <>
                      Disabling {isOneDayRange ? "this day" : "these days"} will
                      prevent workouts from being scheduled {""}
                      {isOneDayRange ? (
                        <>
                          on {""}
                          <Box component={"span"} className={classes.nowrap}>
                            {dateFrom}
                          </Box>
                        </>
                      ) : (
                        <>
                          from {""}
                          <Box component={"span"} className={classes.nowrap}>
                            {dateFrom}
                          </Box>
                          {""} to {""}
                          <Box component={"span"} className={classes.nowrap}>
                            {dateTo}
                          </Box>
                        </>
                      )}
                      {""} at {""}
                      <Box component={"span"} className={classes.facilityName}>
                        {facilityName}.
                      </Box>
                    </>
                  )}
                  {isRepeatWeekly && (
                    <>
                      Disabling these days will prevent workouts from being
                      scheduled every{" "}
                      {isOneDayRange
                        ? dayOfWeek
                        : `${dayOfWeek} to ${format(endDate, "iiii")}`}{" "}
                      beginning {""}
                      <Box component={"span"} className={classes.nowrap}>
                        {dateFrom}
                      </Box>
                      {""} at {""}
                      <Box component={"span"} className={classes.facilityName}>
                        {facilityName}.
                      </Box>
                    </>
                  )}
                </Typography>
                <Box className={classes.controls}>
                  <Button
                    variant={"outlined"}
                    className={classes.control}
                    onClick={handleClickBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant={"outlined"}
                    className={clsx(classes.control, classes.confirmControl)}
                    onClick={handleConfirm}
                  >
                    Disable
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box className={classes.content}>
              <Box className={classes.header}>
                <Typography className={classes.headerTitle}>
                  {isNoRepeat && <>Enable Day</>}
                  {isRepeatWeekly && <>Enable Days</>}
                </Typography>
                <IconButton
                  size={"small"}
                  className={classes.closeButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Typography className={classes.description}>
                {isNoRepeat && (
                  <>
                    Enabling {dateFrom} will allow workouts to be scheduled on
                    this day at {""}
                    <Box component={"span"} className={classes.facilityName}>
                      {facilityName}.
                    </Box>
                  </>
                )}
                {isRepeatWeekly && (
                  <>
                    {dateFrom} is part of a repeating series of days.
                    <Box component={"span"} className={classes.subtitle}>
                      Series
                    </Box>
                    <Box component={"span"} className={classes.dateBold}>
                      Weekly on {dayOfWeek} beginning {""}
                      <Box component={"span"} className={classes.nowrap}>
                        {format(
                          parseISO(selectedDay.facilityBlackout.dateFrom),
                          "MMMM d, yyyy"
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </Typography>

              {isRepeatWeekly && (
                <>
                  <Typography className={classes.listItemTitle}>
                    Select Dates to Enable
                  </Typography>
                  <List disablePadding>
                    <ListItem button className={classes.listItem}>
                      <Button
                        fullWidth
                        className={classes.listItemButton}
                        onClick={handleEnableOptionSelect(EnableOption.DayOnly)}
                      >
                        {selectedEnableOption === EnableOption.DayOnly ? (
                          <RadioButtonCheckedIcon
                            className={classes.listItemCheckedIcon}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            className={classes.listItemUncheckedIcon}
                          />
                        )}
                        <span className={classes.listItemText}>
                          {dateFromShort} only
                        </span>
                      </Button>
                    </ListItem>
                    <ListItem button className={classes.listItem}>
                      <Button
                        fullWidth
                        className={classes.listItemButton}
                        onClick={handleEnableOptionSelect(
                          EnableOption.DayAndAfter
                        )}
                      >
                        {selectedEnableOption === EnableOption.DayAndAfter ? (
                          <RadioButtonCheckedIcon
                            className={classes.listItemCheckedIcon}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            className={classes.listItemUncheckedIcon}
                          />
                        )}
                        <span className={classes.listItemText}>
                          {dateFromShort} and all following days in series
                        </span>
                      </Button>
                    </ListItem>
                    <ListItem button className={classes.listItem}>
                      <Button
                        fullWidth
                        className={classes.listItemButton}
                        onClick={handleEnableOptionSelect(EnableOption.Serie)}
                      >
                        {selectedEnableOption === EnableOption.Serie ? (
                          <RadioButtonCheckedIcon
                            className={classes.listItemCheckedIcon}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            className={classes.listItemUncheckedIcon}
                          />
                        )}
                        <span className={classes.listItemText}>
                          Entire series
                        </span>
                      </Button>
                    </ListItem>
                  </List>
                </>
              )}

              <Box className={classes.controls}>
                <Button
                  variant={"outlined"}
                  className={classes.control}
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  variant={"outlined"}
                  className={clsx(classes.control, classes.enableControl)}
                  onClick={handleClickEnable}
                >
                  Enable
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default BlackoutDatePopover;
